import { useState } from 'react';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { KeyRound, ShieldAlert, ShieldCheck } from 'lucide-react';
import clsx from 'clsx';
import { apiClient, ApiError } from '@/lib/api';
import { buildAsrWriteBody } from '@/lib/asr-credential-form';
import { Card } from '@/components/ui/card';
import { ErrorState, LoadingState } from '@/components/ui/empty-state';
import { DeleteWithCaptchaModal } from '@/components/ui/delete-captcha';
import type { DeleteCaptchaProof } from '@/lib/captcha-api';
import type {
  AsrCredentialClearResult,
  AsrCredentialFieldKey,
  AsrCredentialStatus,
  AsrCredentialWriteResult,
  WriteAsrCredentialsRequest,
} from '@/lib/types';

/**
 * Issue #180 — 管理员「系统设置 → 语音识别密钥」写入/替换/清空卡片。
 *
 * 红线：
 *   - 永不回显已有密钥明文：界面只显示「已配置/未配置 + 最近更新时间 + 操作者」；
 *   - 输入框仅用于写入新值，保存成功后立即清空本地状态；
 *   - 绝不写入 localStorage/sessionStorage；
 *   - 仅 ADMIN/SUPER_ADMIN 可用（后端 RBAC 双重拦截）。
 */

const INPUT =
  'w-full rounded-md border border-slate-300 px-2.5 py-1.5 text-sm focus:border-brand-500 focus:outline-none focus:ring-2 focus:ring-brand-100';
const PRIMARY_BTN =
  'inline-flex items-center gap-2 rounded-md bg-brand-500 px-3 py-1.5 text-sm font-medium text-white transition hover:bg-brand-600 disabled:opacity-60';
const SECONDARY_BTN =
  'inline-flex items-center gap-2 rounded-md border border-slate-300 bg-white px-3 py-1.5 text-sm text-slate-600 transition hover:bg-slate-50 disabled:opacity-60';

interface FieldDef {
  key: AsrCredentialFieldKey;
  label: string;
  envName: string;
}
interface ProviderGroup {
  title: string;
  fields: FieldDef[];
}

const GROUPS: ProviderGroup[] = [
  {
    title: '科大讯飞 WebAPI (IAT / OST / 实时标准版)',
    fields: [
      { key: 'xfyunAppId', label: 'APP ID', envName: 'XFYUN_APP_ID' },
      { key: 'xfyunApiKey', label: 'API Key', envName: 'XFYUN_API_KEY' },
      { key: 'xfyunApiSecret', label: 'API Secret', envName: 'XFYUN_API_SECRET' },
    ],
  },
  {
    // Issue #258 — 录音文件转写标准版 RAASR v2 专用 secretKey（与 WebAPI apiSecret 不同）。
    title: '科大讯飞录音文件转写标准版 (RAASR)',
    fields: [
      {
        key: 'xfyunSecretKey',
        label: 'Secret Key',
        envName: 'XFYUN_FILE_STANDARD_SECRET_KEY',
      },
    ],
  },
  {
    // Issue #258 / #265 — iflyaisol 新平台 accessKey 对（录音文件大模型 + 实时大模型共用）。
    title: '科大讯飞大模型 (录音文件 / 实时，iflyaisol)',
    fields: [
      {
        key: 'xfyunAccessKeyId',
        label: 'Access Key ID',
        envName: 'XFYUN_AISOL_ACCESS_KEY_ID',
      },
      {
        key: 'xfyunAccessKeySecret',
        label: 'Access Key Secret',
        envName: 'XFYUN_AISOL_ACCESS_KEY_SECRET',
      },
    ],
  },
  {
    title: '阿里云百炼 (Aliyun Bailian)',
    fields: [
      {
        key: 'aliyunApiKey',
        label: 'API Key',
        envName: 'ALIYUN_BAILIAN_API_KEY / DASHSCOPE_API_KEY',
      },
    ],
  },
];

function fmtTime(iso: string | null): string {
  if (!iso) return '—';
  const d = new Date(iso);
  if (Number.isNaN(d.getTime())) return '—';
  const pad = (n: number) => String(n).padStart(2, '0');
  return `${d.getFullYear()}-${pad(d.getMonth() + 1)}-${pad(d.getDate())} ${pad(d.getHours())}:${pad(d.getMinutes())}`;
}

function errMessage(err: unknown): string {
  if (err instanceof ApiError) return err.status > 0 ? `${err.message}（HTTP ${err.status}）` : err.message;
  return err instanceof Error ? err.message : '操作失败，请稍后重试。';
}

export function AsrCredentialSettingsCard({ canEdit }: { canEdit: boolean }) {
  const queryClient = useQueryClient();
  const [drafts, setDrafts] = useState<Partial<Record<AsrCredentialFieldKey, string>>>({});
  const [note, setNote] = useState<string | null>(null);
  const [error, setError] = useState<string | null>(null);

  const query = useQuery({
    queryKey: ['admin', 'asr', 'credentials'],
    enabled: canEdit,
    queryFn: () => apiClient.get<AsrCredentialStatus>('/admin/asr/credentials'),
  });

  const refresh = () =>
    queryClient.invalidateQueries({ queryKey: ['admin', 'asr', 'credentials'] });

  const writeMutation = useMutation({
    mutationFn: (body: WriteAsrCredentialsRequest) =>
      apiClient.request<AsrCredentialWriteResult>('/admin/asr/credentials', {
        method: 'PUT',
        body: JSON.stringify(body),
      }),
    onSuccess: async (res) => {
      setError(null);
      // 安全红线：保存后立即清空本地明文草稿，绝不持久化。
      setDrafts({});
      setNote(
        res.updatedFields.length > 0
          ? `已更新：${res.updatedFields.join('、')}`
          : '没有需要更新的字段（输入为空）',
      );
      await refresh();
    },
    onError: (err) => {
      setNote(null);
      setError(errMessage(err));
    },
  });

  // Issue #235：清空 = 删除运行时密钥，必须先过后端图形验证码（asr-credential:clear）。
  // 点击「清空本组 / 清空」打开验证码弹窗，确认时随 DELETE body 回传 captchaId+captchaCode；
  // 后端 consume 校验失败抛 400，绝不清空。绝不前端本地校验。
  const [clearTarget, setClearTarget] = useState<{
    fields: AsrCredentialFieldKey[];
    label: string;
  } | null>(null);

  async function runClear(proof: DeleteCaptchaProof) {
    if (!clearTarget) return;
    const res = await apiClient.request<AsrCredentialClearResult>('/admin/asr/credentials', {
      method: 'DELETE',
      body: JSON.stringify({ fields: clearTarget.fields, ...proof }),
    });
    setError(null);
    setDrafts({});
    setNote(
      res.clearedFields.length > 0
        ? `已清空：${res.clearedFields.join('、')}`
        : '没有可清空的字段',
    );
    await refresh();
  }

  if (!canEdit) {
    return (
      <Card title="语音识别密钥">
        <p className="text-sm text-slate-500">
          ASR 密钥管理仅 ADMIN / SUPER_ADMIN 可用。
        </p>
      </Card>
    );
  }

  const status = query.data;
  const pending = writeMutation.isPending;

  function setDraft(key: AsrCredentialFieldKey, value: string) {
    setDrafts((prev) => ({ ...prev, [key]: value }));
  }

  function saveGroup(group: ProviderGroup) {
    const body = buildAsrWriteBody(
      drafts,
      group.fields.map((f) => f.key),
    );
    setNote(null);
    setError(null);
    writeMutation.mutate(body);
  }

  return (
    <Card
      title="语音识别密钥"
      description="写入 / 替换 / 清空 ASR provider 运行时密钥。服务端 AES-256-GCM 加密存储，运行时即时生效（无需重启）。界面永不回显已有密钥明文。"
    >
      {query.isLoading ? (
        <LoadingState label="正在加载密钥状态…" />
      ) : query.isError ? (
        <ErrorState
          title="密钥状态加载失败"
          description={errMessage(query.error)}
          retry={() => void query.refetch()}
        />
      ) : (
        <div className="flex flex-col gap-6">
          {GROUPS.map((group) => (
            <section key={group.title} className="rounded-lg border border-slate-200 p-4">
              <div className="mb-3 flex items-center justify-between gap-2">
                <h3 className="flex items-center gap-2 text-sm font-semibold text-slate-900">
                  <KeyRound className="h-4 w-4 text-slate-400" />
                  {group.title}
                </h3>
                <button
                  type="button"
                  className={SECONDARY_BTN}
                  disabled={pending}
                  onClick={() => {
                    setNote(null);
                    setError(null);
                    setClearTarget({
                      fields: group.fields.map((f) => f.key),
                      label: group.title,
                    });
                  }}
                >
                  清空本组
                </button>
              </div>

              <div className="flex flex-col gap-3">
                {group.fields.map((f) => {
                  const s = status?.[f.key];
                  const configured = !!s?.configured;
                  return (
                    <div key={f.key} className="grid grid-cols-1 gap-2 md:grid-cols-[1fr_1.4fr]">
                      <div>
                        <div className="flex items-center gap-2 text-sm font-medium text-slate-800">
                          {f.label}
                          <span
                            className={clsx(
                              'inline-flex items-center gap-1 rounded-full px-2 py-0.5 text-[10px] font-medium',
                              configured
                                ? 'bg-emerald-50 text-emerald-700'
                                : 'bg-slate-100 text-slate-500',
                            )}
                          >
                            {configured ? (
                              <>
                                <ShieldCheck className="h-3 w-3" /> 已配置
                              </>
                            ) : (
                              <>
                                <ShieldAlert className="h-3 w-3" /> 未配置
                              </>
                            )}
                          </span>
                        </div>
                        <div className="mt-0.5 text-[11px] text-slate-400">
                          环境变量回退：{f.envName}
                        </div>
                        {configured ? (
                          <div className="mt-0.5 text-[11px] text-slate-400">
                            更新：{fmtTime(s?.updatedAt ?? null)} · 操作者{' '}
                            {s?.updatedByName || s?.updatedBy || '—'}
                          </div>
                        ) : null}
                      </div>
                      <div className="flex items-center gap-2">
                        <input
                          className={INPUT}
                          type="password"
                          autoComplete="new-password"
                          placeholder={configured ? '输入新值以替换（留空不修改）' : '输入密钥以写入'}
                          value={drafts[f.key] ?? ''}
                          onChange={(e) => setDraft(f.key, e.target.value)}
                        />
                        {configured ? (
                          <button
                            type="button"
                            className={SECONDARY_BTN}
                            disabled={pending}
                            onClick={() => {
                              setNote(null);
                              setError(null);
                              setClearTarget({ fields: [f.key], label: f.label });
                            }}
                          >
                            清空
                          </button>
                        ) : null}
                      </div>
                    </div>
                  );
                })}
              </div>

              <div className="mt-3">
                <button
                  type="button"
                  className={PRIMARY_BTN}
                  disabled={pending}
                  onClick={() => saveGroup(group)}
                >
                  {writeMutation.isPending ? '保存中…' : '保存密钥'}
                </button>
              </div>
            </section>
          ))}

          {note ? (
            <div className="rounded-md border border-emerald-200 bg-emerald-50 px-3 py-2 text-xs text-emerald-700">
              {note}
            </div>
          ) : null}
          {error ? (
            <div className="rounded-md border border-rose-200 bg-rose-50 px-3 py-2 text-xs text-rose-700">
              {error}
            </div>
          ) : null}
        </div>
      )}

      {clearTarget ? (
        <DeleteWithCaptchaModal
          key={`asr-clear-${clearTarget.label}`}
          action="asr-credential:clear"
          title={`清空 ASR 密钥 · ${clearTarget.label}`}
          description="清空后该密钥运行时立即失效（仅环境变量回退仍可用）。清空前需通过图形验证码防误删。"
          confirmLabel="确认清空"
          onClose={() => setClearTarget(null)}
          onConfirm={runClear}
        />
      ) : null}
    </Card>
  );
}
